import React, { useState, useRef } from 'react';

interface ServerItemProps {
  server: {
    id: string;
    name: string;
    icon_url: string | null;
  };
  isActive: boolean;
  onClick: () => void;
  unreadCount?: number;
}

const ServerItem: React.FC<ServerItemProps> = ({ server, isActive, onClick, unreadCount = 0 }) => {
  const hasIcon = !!server.icon_url;
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipY, setTooltipY] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setTooltipY(rect.top + rect.height / 2);
    }
    setShowTooltip(true);
  };

  return (
    <div className="relative" ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={() => setShowTooltip(false)}>
      {/* Active indicator pill */}
      <div className={`absolute -left-4 top-1/2 -translate-y-1/2 w-1 rounded-r-full bg-text-primary transition-all duration-200 ${
        isActive ? 'h-5' : unreadCount > 0 ? 'h-2' : 'h-0'
      }`} />

      {/* Server icon button */}
      {server.icon_url ? (
        /* — has icon: outer=ring only (no bg), inner=overflow clip — */
        <div
          className={`w-11 h-11 cursor-pointer transition-all duration-200 ${
            isActive
              ? 'rounded-xl ring-2 ring-accent-color ring-offset-2 ring-offset-[#0F0F0F]'
              : 'rounded-2xl hover:rounded-xl'
          }`}
          onClick={onClick}
        >
          <div className={`w-full h-full overflow-hidden transition-all duration-200 ${isActive ? 'rounded-xl' : 'rounded-2xl hover:rounded-xl'}`}>
            <img src={server.icon_url} alt={server.name} className="w-full h-full object-cover" />
          </div>
        </div>
      ) : (
        /* — no icon: show initial letter with background — */
        <div
          className={`w-11 h-11 rounded-2xl flex items-center justify-center cursor-pointer transition-all duration-200 ${
            isActive
              ? 'bg-accent-color text-white rounded-xl'
              : 'bg-highlight hover:bg-highlight-hover text-text-primary hover:rounded-xl'
          }`}
          onClick={onClick}
        >
          <span className="text-sm font-semibold">
            {server.name.charAt(0).toUpperCase()}
          </span>
        </div>
      )}

      {/* Unread badge */}
      {unreadCount > 0 && !isActive && (
        <span className="absolute -bottom-1 -right-1 min-w-[16px] h-[16px] bg-error text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 border-2 border-bg-primary">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}

      {/* Tooltip */}
      {showTooltip && (
        <div 
          className="fixed left-[76px] z-[9999] px-3 py-1.5 bg-bg-primary border border-border-color rounded-lg shadow-lg text-sm font-medium text-text-primary whitespace-nowrap pointer-events-none animate-fade-in"
          style={{ top: tooltipY, transform: 'translateY(-50%)' }}
        >
          {server.name}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-bg-primary border-l border-b border-border-color rotate-45" />
        </div>
      )}
    </div>
  );
};

export default ServerItem;